import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CustomSelect from './CustomSelect';
import CustomRadioButton from './CustomRadioButton';
import CustomDatepicker from './CustomDatepicker';
import CustomCheckbox from './CustomCheckbox';
import API_REST from '../../api-rest-uruk';
import { handleStateByPath, itemsExport, itemsExportType, itemsIdiomas, lang, valitionForm } from './utils';
import useSubscribeToExportHistory from './useSubscribeToExportHistory';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { InputLabel, Typography } from '@material-ui/core';
import ListExportData from './ListExportData';
import { useRouteMatch } from 'react-router-dom';

const useStyles = makeStyles(() => ({

  containerInput: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: '10px',
    marginTop: '12px'
  },
  titleInput: {
    width: '150px'
  },
  label: {
    marginRight: '20px'
  },
  title: {
    paddingLeft: '20px',
    paddingTop: '20px',
    paddingRight: '20px',
    fontWeight: 'bold'
  },
  titleTable: {
    paddingLeft: '20px',
    paddingRight: '20px'
  },
  titleDescription: {
    marginRight: '20px',
    marginLeft: '20px',
    fontWeight: 'normal',
    fontSize: '12px',
    fontFamily: 'Roboto-Regular'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '30px',
    paddingRight: '30px',
    paddingTop: '30px',
    paddingBottom: '10px',
    fontWeight: 'bold'
  },
  errorTitle: {
    color: '#f44336',
    textAlign: 'center',
    fontWeight: 'bold'
  },
  containerButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
    marginBottom: '10px'
  },
  containerLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '25px',
    width: '75%',
    margin: 'auto',
    marginTop: '50px',
    textAlign: 'center'
  },
  containerExport: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden'
  }
}));

const initialFormState = {
  dateRange: {
    startDate: '',
    endDate: ''
  },
  exportAs: '',
  exportType: '',
  status: [''],
  createdByMe: false,
  localDate: false,
  includeSubflows: false,
  language: lang[window.localStorage.getItem('i18nextLng')] || 'ES'
};

export default function CustomDrawer ({ type, toggleDrawer }) {
  const group = useSelector(state => state.config.group);
  const subgroups = useSelector(state => state.config.subgroup);
  const { path: pathname } = useRouteMatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const data = useSubscribeToExportHistory({ group, subgroups, type });
  const [state, setState] = React.useState({
    loading: false,
    errors: '',
    formState: { ...initialFormState, type }
  });

  const itemsStates = handleStateByPath(pathname);

  const handleFetch = async (body) => {
    API_REST.exportData(body).then(() => {
      setTimeout(() => {
        setState(prev => ({ ...prev, loading: false }));
      }, 5000);
    }).catch((err) => {
      console.error('error::', err);
    });
  };

  const handleSubmit = (event) => {
    const copyFormState = { ...state.formState };
    try {
      setState({ ...state, loading: true });
      event.preventDefault();
      if (copyFormState.status.includes('')) {
        copyFormState.status = itemsStates.map((item) => item.value);
      }
      copyFormState.exportType = copyFormState.exportType === 'extended';
      const validations = valitionForm(copyFormState, t);
      if (validations && typeof validations === 'string') {
        setState(prev => ({ ...prev, errors: validations, loading: false }));
      } else {
        handleFetch(copyFormState);
      }
    } catch (error) {
      console.error('error::', error);
    }
  };

  const handleSetState = (value, key) => {
    setState(prev => ({ ...prev, formState: { ...prev.formState, [key]: value } }));
  };

  const handleStateDateRange = ({ startDate, endDate }) => {
    setState(prev => ({ ...prev, formState: { ...prev.formState, dateRange: { startDate, endDate } } }));
  };

  return (
    <div role='presentation' className={classes.containerExport}>
      <Typography variant='h4' className={classes.title}>{t('exportData:titleExportData')}</Typography>
      {state.errors && <Typography className={classes.errorTitle} >{state.errors}</Typography>}
      <form className={classes.list} onSubmit={handleSubmit} id='formExport'>
        <div className={classes.containerInput}>
          <InputLabel id='exportAs' className={classes.titleInput}>{t('exportData:exportAs')}</InputLabel>
          <CustomSelect
            menuItems={itemsExport}
            value={state.formState.exportAs}
            onChange={(value) => handleSetState(value, 'exportAs')}
            labelId='exportAs'
            required />
        </div>
        <div className={classes.containerInput}>
          <InputLabel id='exportType' className={classes.titleInput}>{t('exportData:exportType')}</InputLabel>
          <CustomRadioButton
            htmlFor='exportType'
            listItem={itemsExportType}
            value={state.formState.exportType}
            onChange={(value) => handleSetState(value, 'exportType')}
            radioProps={{ required: true }} />
        </div>
        <div className={classes.containerInput}>
          <InputLabel id='date' className={classes.titleInput}>{t('exportData:date')}</InputLabel>
          <CustomDatepicker
            htmlFor='date'
            onChange={(value) => handleStateDateRange(value)} />
        </div>
        <div className={classes.containerInput}>
          <InputLabel id='state' className={classes.titleInput}>{t('exportData:state')}</InputLabel>
          <CustomSelect
            menuItems={itemsStates}
            label={t('exportData:stateLabel')}
            value={state.formState.status}
            onChange={(value) => handleSetState(value, 'status')}
            labelId='state'
            multiple />
        </div>
        <div className={classes.containerInput}>
          <InputLabel id='language' className={classes.titleInput}>{t('exportData:language')}</InputLabel>
          <CustomSelect
            menuItems={itemsIdiomas}
            value={state.formState.language}
            onChange={(value) => handleSetState(value, 'language')}
            labelId='language' />
        </div>

        <CustomCheckbox
          label={t('exportData:createdByMe')}
          value={state.formState.createdByMe}
          onChange={(value) => handleSetState(value, 'createdByMe')} />
        <CustomCheckbox
          label={t('exportData:localDate')}
          value={state.formState.localDate}
          onChange={(value) => handleSetState(value, 'localDate')} />
        {pathname === '/bundle' && <CustomCheckbox
          label={t('exportData:includeSubflows')}
          value={state.formState.includeSubflows}
          onChange={(value) => handleSetState(value, 'includeSubflows')} />}
        <div className={classes.containerButton}>
          <Button
            style={{ marginRight: '40px' }}
            variant='contained'
            color='secondary'
            onClick={() => toggleDrawer(false)}>
            {t('exportData:cancel')}
          </Button>
          <Button
            disabled={state.loading}
            type='submit'
            form='formExport'
            variant='contained'
            color='primary'>
            {t('exportData:export')}
          </Button>
        </div>
      </form>
      <Typography variant='h6' className={classes.titleTable}>{t('exportData:titleExportDataHistory')}</Typography>
      <Typography variant='subtitle2' className={classes.titleDescription} >{t('exportData:descriptionExportDataHistory')}</Typography>
      <ListExportData data={data} />
    </div>
  );
}

CustomDrawer.propTypes = {
  type: PropTypes.string.isRequired,
  toggleDrawer: PropTypes.func.isRequired
};
